import { z } from 'zod'
import { ERINNERUNG_STUNDE } from './erinnerung'

/**
 * **Vorwarnung vor einem Termin** ([Z-13](../../../docs/phasen/zusatz-13-frist-vorwarnung.md)).
 *
 * Nils, 28.08.2026: *„Bei Fälligkeit möchte ich auch einstellen ob ich x Tage vorher erinnert
 * werden will per Mail und per System."* Und zur Frage, was voreingestellt sein soll: *„das
 * wählt derjenige selber bei der Aufgabe aus. Wenn ich das bspw. bei einer bestimmten Aufgabe
 * anhake ‚Benachrichtigen', dann gilt für mich die Einstellung bspw. ‚3 Tage vorher' … wenn es
 * jemand anders da einträgt – dann gilt das nur für ihn. Zusätzlich würde ich anbieten:
 * Benachrichtigung an alle Bearbeiter."*
 *
 * ## Die zwei Hälften, die nicht verwechselt werden dürfen
 *
 * | | wo | Bedeutung |
 * |---|---|---|
 * | **Das Abo** | an der Aufgabe, je Person (`frist_abo`) | *ob* ich an diese Aufgabe erinnert werde |
 * | **Die Einstellung** | an der Person (`person.frist_vorwarn_*`) | *wann* und *wie* – meine Vorwarnzeit, meine Kanäle |
 *
 * Deshalb gibt es keine Vorwarnzeit an der Aufgabe. Zwei Leute können dieselbe Aufgabe
 * abonnieren und werden zu verschiedenen Zeiten erinnert – genau so beauftragt. Stünde die
 * Zahl an der Aufgabe, überschriebe der Zweite dem Ersten seine Frist, ohne es zu merken.
 *
 * ## Warum das hier steht und nicht im Zeitgeber
 *
 * Dieselbe eiserne Regel wie in `erinnerung.ts`, `schirm.ts` und `meintag.ts`: **kein
 * `new Date()` ohne Übergabe.** Eine Regel, für deren Prüfung man drei Tage warten muss, wird
 * nicht geprüft. Als reine Funktion mit übergebenem Stichtag ist sie eine Tabelle aus Fällen.
 */

/**
 * **Die Vorwarnzeiten, die als Knopf dastehen** – die Abkürzung, nicht die Grenze
 * ([E-303](../../../docs/ENTSCHEIDUNGEN.md#e-303)).
 *
 * Nils, 02.09.2026: *„Bei den Erinnerungen da war «3 Tage» als Beispiel, da sollte ich 1, 3, 5,
 * 10 Tage auswählen können (oder flexibel eingebbar 1–99). Standard können ja die drei sein."*
 *
 * Die erste Fassung bot `[0, 1, 2, 3, 5, 7, 14]` und **nur** das. Das war die falsche Hälfte
 * der Entscheidung: die Liste sagte nicht „diese Zahlen reichen", sondern „eine andere Zahl
 * gibt es nicht" – und wer sechs Wochen vor einem Termin gewarnt werden will, weil daran ein
 * Angebot hängt, stand vor einer Wand aus sieben Knöpfen.
 *
 * Jetzt beides: die Knöpfe für die Zahlen, die man ohne Nachdenken nimmt, und daneben ein Feld
 * für die, die man begründet. Wer nichts anfasst, sieht dieselbe Auswahl wie vorher.
 *
 * **`0` ist dabei und heißt „am Fälligkeitstag selbst"** – nicht „aus". Aus ist, wer die
 * Aufgabe nicht abonniert hat; das ist eine Entscheidung an der Aufgabe und keine Zahl.
 */
export const VORWARN_TAGE_OPTIONEN = [0, 1, 3, 5, 10] as const

/** Voreinstellung: **drei Tage** – Nils' eigenes Beispiel, und es überlebt ein Wochenende. */
export const VORWARN_STANDARD_TAGE = 3

/**
 * **Zu welcher Uhrzeit gewarnt wird** – dieselbe Stunde wie bei der Abnahme-Erinnerung.
 *
 * Bewusst nicht einstellbar und bewusst **dieselbe** Konstante, nicht eine zweite mit demselben
 * Wert: zwei Uhrzeiten für zwei Mailsorten wären zwei Stellschrauben, die niemand verstellt und
 * die jede Fehlersuche verdoppeln.
 */
export const VORWARN_STUNDE = ERINNERUNG_STUNDE

/**
 * **Wie ich gewarnt werden will** – die persönliche Hälfte.
 *
 * Beide Kanäle können aus sein. Das ist kein Unsinn, sondern die Bremse: wer die Mail nicht
 * will und die Zeile in „Klopft an" auch nicht, hat die Vorwarnung abgeschaltet, ohne an jeder
 * einzelnen Aufgabe das Häkchen wegnehmen zu müssen. Die Abos bleiben stehen und wirken wieder,
 * sobald ein Kanal zurückkommt.
 */
export interface Vorwarneinstellung {
  /** Kalendertage vor dem Termin. `0` = am Termin selbst. */
  tage: number
  /** Eine Mail an die hinterlegte Adresse. */
  perMail: boolean
  /** Eine Zeile in „Klopft an" auf „Mein Tag". */
  imSystem: boolean
}

/**
 * **Die Obergrenze der freien Eingabe: 99 Tage** – so beauftragt (Nils, 02.09.2026: *„flexibel
 * eingebbar 1–99"*).
 *
 * Bewusst **nicht** aus {@link VORWARN_TAGE_OPTIONEN} gerechnet, wie es vorher der Fall war:
 * die Knopfliste ist eine Bequemlichkeit und darf sich ändern, ohne dass dabei still die
 * Eingabeprüfung mitwandert und gespeicherte Werte ungültig macht. Zwei verschiedene Fragen,
 * zwei Konstanten.
 *
 * Eine Grenze braucht es trotzdem: `frist_vorwarn_tage` ist ein `INT`, und „3650 Tage vorher"
 * wäre keine Vorwarnung, sondern eine Dauererinnerung an jede abonnierte Aufgabe.
 */
export const VORWARN_TAGE_MAX = 99

export const vorwarnTageSchema = z.number().int().min(0).max(VORWARN_TAGE_MAX)

export const vorwarneinstellungSchema = z.object({
  tage: vorwarnTageSchema,
  perMail: z.boolean(),
  imSystem: z.boolean(),
})

/** Ein Kanal ist an – sonst ist die Vorwarnung stumm, gleich wie viele Abos dastehen. */
export function vorwarnungAktiv(einstellung: Vorwarneinstellung): boolean {
  return einstellung.perMail || einstellung.imSystem
}

/** Der Tagesbeginn – Ortszeit, weil ein Termin ein Tag ist und keine Uhrzeit. */
function tagesbeginn(zeitpunkt: Date): Date {
  return new Date(zeitpunkt.getFullYear(), zeitpunkt.getMonth(), zeitpunkt.getDate())
}

/**
 * **Kalendertage bis zum Termin.** Negativ heißt überfällig, `0` heißt heute.
 *
 * Kalendertage und nicht verstrichene Stunden geteilt durch 24 – dieselbe Überlegung wie bei
 * `tageSeit` in der Abnahme-Erinnerung: ein Termin am Freitag ist am Donnerstagabend „morgen"
 * und nicht „in 0,6 Tagen". Wer hier rundet statt Tage zu zählen, ist am Tagesrand um einen
 * Tag daneben, und zwar in der Richtung, die zu spät warnt.
 */
export function tageBis(termin: Date, jetzt: Date): number {
  return Math.round((tagesbeginn(termin).getTime() - tagesbeginn(jetzt).getTime()) / 86_400_000)
}

/**
 * **Liegt der Termin im Vorwarnfenster?** – die Frage des System-Kanals.
 *
 * Von der Vorwarnung bis zum Termin **einschließlich beider Enden**. Was schon vorbei ist,
 * gehört nicht hierher: dafür gibt es „Überfällig", und dieselbe Aufgabe zweimal in derselben
 * Zone wäre eine Zeile, die man zweimal wegsehen muss.
 */
export function imVorwarnfenster(termin: Date, tage: number, jetzt: Date): boolean {
  const bis = tageBis(termin, jetzt)
  return bis >= 0 && bis <= tage
}

/**
 * **Die Zeitpunkte, zu denen eine Mail hinausgeht** – höchstens zwei je Aufgabe und Person.
 *
 * 1. **Die Vorwarnung**, `tage` Kalendertage vor dem Termin, zur festen Stunde.
 * 2. **Der Termin selbst**, zur selben Stunde.
 *
 * ## Warum genau zwei und nicht täglich
 *
 * Weil eine Erinnerung, die bei sieben Tagen Vorwarnzeit siebenmal kommt, nach dem dritten Mal
 * eine Filterregel im Postfach ist. Und weil eine, die nur einmal kommt, ausgerechnet die
 * verpasst, die man am Tag der Vorwarnung überflogen und dann vergessen hat. Zwei ist die
 * kleinste Zahl, die beides aushält: eine Ansage und eine Erinnerung daran.
 *
 * Bei `tage === 0` fallen beide zusammen; dann ist es **ein** Zeitpunkt und nicht zweimal
 * derselbe.
 */
export function vorwarnZeitpunkte(
  termin: Date,
  tage: number,
): { art: 'vorwarnung' | 'faellig'; wann: Date }[] {
  const amTermin = tagesbeginn(termin)
  amTermin.setHours(VORWARN_STUNDE, 0, 0, 0)

  if (tage <= 0) return [{ art: 'faellig', wann: amTermin }]

  const vorher = new Date(amTermin)
  vorher.setDate(vorher.getDate() - tage)
  return [
    { art: 'vorwarnung', wann: vorher },
    { art: 'faellig', wann: amTermin },
  ]
}

/**
 * **Welche Vorwarnung seit dem letzten Lauf fällig geworden ist** – oder `null`.
 *
 * Gefragt wird **rückwärts**, wie bei der Abnahme-Erinnerung, und aus demselben Grund:
 *
 * > Ein Takt, der vorwärts rechnet („heute ist der Vorwarntag"), **verschluckt** die Warnung,
 * > wenn der Prozess an diesem Tag stand. Sie fällt dann lautlos aus, und eine Erinnerung, die
 * > manchmal ausfällt, ist schlimmer als keine.
 *
 * Rückwärts lautet dieselbe Frage: *ist seit dem letzten Lauf ein Zeitpunkt vergangen?* Darauf
 * gibt es auch am übernächsten Tag noch eine Antwort. Einen Tag zu spät gewarnt ist unendlich
 * viel besser als gar nicht.
 *
 * Fallen **beide** Zeitpunkte in dasselbe Fenster (der Server stand eine Woche), gewinnt der
 * **spätere**: „ist heute fällig" ist die Auskunft, die dann noch stimmt.
 */
export function vorwarnungSeit(
  termin: Date,
  tage: number,
  jetzt: Date,
  zuletzt: Date,
): 'vorwarnung' | 'faellig' | null {
  let treffer: 'vorwarnung' | 'faellig' | null = null
  for (const z of vorwarnZeitpunkte(termin, tage)) {
    if (z.wann > zuletzt && z.wann <= jetzt) treffer = z.art
  }
  return treffer
}

/**
 * **Wie der Termin in Worten dasteht** – für die Zeile in „Klopft an" und für die Mail.
 *
 * „fällig in 3 Tagen" und nicht „fällig am 31.08.2026": die Zahl ist die Auskunft, das Datum
 * muss man erst gegen den Kalender halten. Ab einer Woche kippt es, weil „in 12 Tagen" nichts
 * mehr auslöst – dann steht das Datum daneben.
 */
export function vorwarnHinweis(termin: Date, jetzt: Date): string {
  const bis = tageBis(termin, jetzt)
  if (bis < 0) return bis === -1 ? 'seit gestern fällig' : `seit ${-bis} Tagen fällig`
  if (bis === 0) return 'heute fällig'
  if (bis === 1) return 'morgen fällig'
  if (bis <= 7) return `fällig in ${bis} Tagen`
  return `fällig in ${bis} Tagen (${termin.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
  })})`
}

/**
 * **„3 Tage vorher"** – die Vorwarnzeit allein, ohne Kanäle.
 *
 * Eigene Funktion, weil sie an zwei sehr verschiedenen Orten steht: auf dem Chip im
 * Aufgabendetail (wo neben ihr kein Platz für mehr ist) und als erste Hälfte von
 * {@link vorwarnungBeschreiben}. Zwei Schreibweisen für dieselbe Zahl wären der Anfang von
 * „heißt das dasselbe?".
 */
export function vorwarnZeitText(tage: number): string {
  if (tage === 0) return 'Am Fälligkeitstag'
  if (tage === 1) return '1 Tag vorher'
  return `${tage} Tage vorher`
}

/** „3 Tage vorher, per Mail und im System" – für die Oberfläche und den Fuß der Mail. */
export function vorwarnungBeschreiben(einstellung: Vorwarneinstellung): string {
  const kanaele = [
    einstellung.perMail ? 'per Mail' : null,
    einstellung.imSystem ? 'im System' : null,
  ].filter(Boolean)

  if (kanaele.length === 0) return 'Vorwarnung abgeschaltet'

  return `${vorwarnZeitText(einstellung.tage)}, ${kanaele.join(' und ')}`
}
